import { useMemo, useState } from "react";
import { Button, Icon, Notice } from "@conuti-das/prince-ui";
import { Flex, princeIcon } from "@/ui-prince";

interface Props {
  /** Aus /api/validator/convert-and-validate; bei MIG-Treffer eine strukturierte
      ``MappedMessage`` (mit Segments-Hierarchie + AHB-Anreicherung), sonst flat. */
  json: unknown;
}

/**
 * Zeigt das vom Validator-Service zurückgegebene EDI-JSON (pretty-printed,
 * scrollbar, mit Kopieren-Button). Wird in ValidatorPage in einem Tab gerendert
 * — daher kein eigenes Card-Wrapping nötig.
 */
export function EdiJsonView({ json }: Props) {
  const [copied, setCopied] = useState(false);

  const pretty = useMemo(() => JSON.stringify(json, null, 2), [json]);
  const lineCount = useMemo(() => pretty.split("\n").length, [pretty]);
  const sizeKb = useMemo(() => (pretty.length / 1024).toFixed(1), [pretty]);

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(pretty);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard-API kann unter http (lokal) blockiert sein -- still failen,
      // der Reviewer kann immer noch markieren + Ctrl+C.
    }
  }

  if (json === null || json === undefined) {
    return (
      <Notice tone="info">
        Kein EDI-JSON vorhanden — Konvertierung lieferte keinen Output.
      </Notice>
    );
  }

  return (
    <Flex direction="Column" style={{ gap: "var(--s-2)" }}>
      <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
        <span style={{ color: "var(--sapContent_LabelColor)", fontSize: "0.78rem", flex: 1 }}>
          {lineCount} Zeilen · {sizeKb} kB · MIG/AHB-angereichert
        </span>
        <Button
          variant="plain"
          onPress={() => void copyToClipboard()}
          aria-label="JSON in Zwischenablage kopieren"
        >
          <Icon name={princeIcon("copy")} size={16} />
          {copied ? "Kopiert!" : "Kopieren"}
        </Button>
      </Flex>
      <pre
        style={{
          margin: 0,
          padding: "var(--s-3)",
          background: "var(--sapList_HeaderBackground, #f5f7fa)",
          border: "1px solid var(--sapGroup_ContentBorderColor)",
          borderRadius: "var(--sapElement_BorderCornerRadius, 4px)",
          fontFamily: "var(--sapFontMonoFamily, monospace)",
          fontSize: "0.78rem",
          lineHeight: 1.4,
          color: "var(--sapTextColor)",
          maxHeight: 540,
          overflow: "auto",
          whiteSpace: "pre",
        }}
      >
        {pretty}
      </pre>
    </Flex>
  );
}
